import React from 'react';
import { partial } from 'lodash';
import { Alert } from 'reactstrap';
import { InputItem } from '../../Forms/InputItem';
import { SwitchItem } from '../../Forms/SwitchItem';
import DatePicker from '../../DatePicker';
import { getTranslationKey } from '../../../modules/utils';
import { SalesCouponSelection } from '../SalesCouponSelection';
import { IShopSalesAutomation } from '../../../modules/types';
import { InlineForm, SmallInputWrapper } from './styles';
import { SWITCH_DIRECTION, SUPPORT_LINKS } from './constants';

interface IProps {
  couponCodeAdvertising: IShopSalesAutomation['couponCodeAdvertising'];
  isFormDisabled: boolean;
  scheduleType: 'datepicker' | 'input';
  couponStartDateChanged: boolean;
  couponEndDateChanged: boolean;
  isStrategiesOverlap: boolean;
  dateFromDays: (days: number) => string;
  updateFormValue: (path: string, value: unknown) => void;
  updateStartDate: (dateStr: string) => void;
  updateEndDate: (dateStr: string) => void;
}

export const CouponCodeAdvertisingSection = React.memo(
  ({
    couponCodeAdvertising,
    isFormDisabled,
    scheduleType,
    couponStartDateChanged,
    couponEndDateChanged,
    isStrategiesOverlap,
    dateFromDays,
    updateFormValue,
    updateStartDate,
    updateEndDate
  }: IProps) => {
    const { startsAt, endsAt } = couponCodeAdvertising || {};

    return (
      <>
        <SwitchItem
          value={couponCodeAdvertising?.enabled}
          changeCallback={partial(updateFormValue, 'couponCodeAdvertising.enabled')}
          label={getTranslationKey('collectionShop.couponToggleLabel')}
          direction={SWITCH_DIRECTION}
          margin="15px 0 5px 0"
          id="couponCodeAdvertising-switch"
          withTooltip
          tooltipText={getTranslationKey('learnMoreLink', {
            link: SUPPORT_LINKS.codeAdv,
            text_link: SUPPORT_LINKS.codeAdv
          })}
          isIconTooltip
        />
        {isStrategiesOverlap && (
          <Alert color="primary" style={{ marginLeft: '38px' }}>
            {getTranslationKey('collectionShop.anniversaryVoucherIsActive')}
          </Alert>
        )}

        <InlineForm offsetLeft>
          <SalesCouponSelection
            value={String(couponCodeAdvertising?.couponId)}
            changeCallback={partial(updateFormValue, 'couponCodeAdvertising.couponId')}
          />
          <SmallInputWrapper>
            <InputItem
              label={getTranslationKey('collectionShop.frequency')}
              type="number"
              min={0}
              max={1000}
              value={couponCodeAdvertising?.amountOfImagesBetweenBanners}
              changeCallback={partial(
                updateFormValue,
                'couponCodeAdvertising.amountOfImagesBetweenBanners'
              )}
              id="couponCodeAdvertising-amountOfImagesBetweenBanners"
              withTooltip
              tooltipText={getTranslationKey('collectionShop.frequencyTooltip')}
              tooltipIconPlacement="label"
            />
          </SmallInputWrapper>
        </InlineForm>

        {scheduleType === 'datepicker' && (
          <InlineForm offsetLeft>
            <SmallInputWrapper>
              <DatePicker
                date={
                  !couponStartDateChanged && startsAt
                    ? startsAt
                    : dateFromDays(couponCodeAdvertising?.startInDays || 0)
                }
                callback={updateStartDate}
                placeholder={getTranslationKey('collectionShop.startOnLabel')}
                label={getTranslationKey('collectionShop.startOnLabel')}
                minDate={new Date()}
                defaultDate={new Date().toString()}
              />
            </SmallInputWrapper>
            <SmallInputWrapper>
              <DatePicker
                date={
                  !couponEndDateChanged && endsAt
                    ? endsAt
                    : dateFromDays(
                        (couponCodeAdvertising?.startInDays ?? 0) +
                          (couponCodeAdvertising?.endsAfterDays ?? 0) || 1
                      )
                }
                callback={updateEndDate}
                placeholder={getTranslationKey('collectionShop.endInLabel')}
                label={getTranslationKey('collectionShop.endInLabel')}
                minDate={new Date(dateFromDays((couponCodeAdvertising?.startInDays ?? 0) + 1 || 1))}
                defaultDate={new Date(dateFromDays((couponCodeAdvertising?.startInDays ?? 0) + 1))}
              />
            </SmallInputWrapper>
          </InlineForm>
        )}

        {scheduleType === 'input' && (
          <InlineForm offsetLeft>
            <SmallInputWrapper>
              <InputItem
                disabled={isFormDisabled}
                label={getTranslationKey('collectionShop.startInLabel')}
                pattern={/^[0-9]+$/i}
                min={0}
                value={couponCodeAdvertising?.startInDays}
                changeCallback={partial(updateFormValue, 'couponCodeAdvertising.startInDays')}
                rightAddonElement={getTranslationKey('collectionShop.daysPostfix')}
                withTooltip={false}
                id="collectionShop-startInDays-daysPostfix"
              />
            </SmallInputWrapper>

            <SmallInputWrapper>
              <InputItem
                disabled={isFormDisabled}
                label={getTranslationKey('collectionShop.endInLabel')}
                pattern={/^[0-9]+$/i}
                min={1}
                value={couponCodeAdvertising?.endsAfterDays}
                changeCallback={partial(updateFormValue, 'couponCodeAdvertising.endsAfterDays')}
                rightAddonElement={getTranslationKey('collectionShop.daysPostfix')}
                withTooltip={false}
                id="collectionShop-endsAfterDays-daysPostfix"
              />
            </SmallInputWrapper>
          </InlineForm>
        )}
      </>
    );
  }
);
